import { useEffect, useState } from "react";
import { api, fmtCurrency, fmtNumber } from "@/lib/api";
import { useCapital } from "@/context/CapitalContext";
import { usePermissions } from "@/context/PermissionsContext";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Camera, ArrowUpRight, ArrowDownRight, Minus, Loader2 } from "lucide-react";
import { displayStatus } from "@/lib/capitalLabels";
import { toast } from "sonner";

const HORIZONS = ["Immediate Attention", "12 Month Plan", "24 Month Plan", "Monitor"];
const H_TONE = {
  "Immediate Attention": "border-rose-200 bg-rose-50/40 text-rose-700",
  "12 Month Plan": "border-amber-200 bg-amber-50/40 text-amber-700",
  "24 Month Plan": "border-blue-200 bg-blue-50/40 text-gbe",
  Monitor: "border-slate-200 bg-slate-50 text-slate-600",
};

function Delta({ now, prior, money = false }) {
  if (prior == null) return <span className="text-[10px] text-slate-400">—</span>;
  const d = (now || 0) - (prior || 0);
  if (Math.abs(d) < 0.005) return <span className="inline-flex items-center text-[11px] text-slate-400"><Minus className="w-3 h-3 mr-0.5" />no change</span>;
  const up = d > 0;
  return (
    <span className={`inline-flex items-center text-[11px] font-medium ${up ? "text-rose-600" : "text-emerald-600"}`}>
      {up ? <ArrowUpRight className="w-3 h-3 mr-0.5" /> : <ArrowDownRight className="w-3 h-3 mr-0.5" />}
      {up ? "+" : ""}{money ? fmtCurrency(d) : fmtNumber(d)}
    </span>
  );
}

export function PriorYearComparison() {
  const { client } = useCapital();
  const { role } = usePermissions();
  const canCapture = ["executive", "admin", "finance_admin"].includes((role || "").toLowerCase());
  const [years, setYears] = useState([]);
  const [year, setYear] = useState("");
  const [data, setData] = useState(null);
  const [wc, setWc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [capturing, setCapturing] = useState(false);

  const load = async (y) => {
    setLoading(true);
    try {
      const params = { client };
      if (y) params.year = y;
      const [ys, cmp, wcr] = await Promise.all([
        api.get("/capital/snapshots/years", { params: { client } }),
        api.get("/capital/snapshots/comparison", { params, timeout: 60000 }),
        api.get("/capital/what-changed", { params: { client }, timeout: 60000 }).catch(() => ({ data: null })),
      ]);
      setYears(ys.data.years || []);
      setData(cmp.data);
      setWc(wcr.data);
      if (!y && cmp.data.prior) setYear(String(cmp.data.prior.year));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load comparison");
    } finally { setLoading(false); }
  };
  useEffect(() => { if (client) load(); /* eslint-disable-next-line */ }, [client]);

  const capture = async () => {
    setCapturing(true);
    try {
      const r = await api.post("/capital/snapshots/capture", {}, { timeout: 120000 });
      toast.success(`Snapshot captured for ${r.data.year} (${r.data.snapshots} registers)`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Snapshot failed");
    } finally { setCapturing(false); }
  };

  if (loading || !data) return <div className="h-64 bg-white border border-slate-200 rounded-lg shimmer" />;

  const prior = data.prior;
  const curH = Object.fromEntries((data.current.horizons || []).map((h) => [h.horizon, h]));
  const priH = prior ? Object.fromEntries((prior.horizons || []).map((h) => [h.horizon, h])) : {};
  const priClasses = prior ? Object.fromEntries((prior.classes || []).map((c) => [c.asset_group, c])) : {};

  return (
    <div className="space-y-5" data-testid="capital-prior-year-tab">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="font-display text-lg font-semibold text-slate-900">Prior-Year Comparison</h2>
        {years.length > 0 && (
          <Select value={year} onValueChange={(v) => { setYear(v); load(v); }}>
            <SelectTrigger className="w-[140px] h-8 bg-white text-xs" data-testid="prior-year-select">
              <SelectValue placeholder="Prior year" />
            </SelectTrigger>
            <SelectContent>
              {years.map((y) => <SelectItem key={y} value={String(y)}>vs {y}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        {canCapture && (
          <Button size="sm" variant="outline" className="h-8 ml-auto" onClick={capture} disabled={capturing}
                  data-testid="prior-year-capture-btn">
            {capturing ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Camera className="w-3.5 h-3.5 mr-1.5" />}
            Capture {new Date().getFullYear()} snapshot
          </Button>
        )}
      </div>

      {!prior ? (
        <div className="bg-white border border-slate-200 rounded-lg p-10 text-center" data-testid="prior-year-empty">
          <p className="text-sm text-slate-600">No prior-year snapshot exists yet for this client.</p>
          <p className="text-xs text-slate-400 mt-2">
            {canCapture
              ? "Capture a snapshot now — next year's assessment will compare against it automatically."
              : "Ask an executive to capture the current assessment as this year's baseline."}
          </p>
        </div>
      ) : (
        <>
          {wc?.has_prior && (wc.changes || []).length > 0 && (
            <div className="rounded-lg border border-blue-200 bg-blue-50/40 p-4" data-testid="what-changed-card">
              <div className="text-[10px] uppercase tracking-widest text-gbe font-semibold">What changed since {wc.prior_year}</div>
              <ul className="mt-2 space-y-1 text-sm text-slate-700 list-disc pl-4">
                {wc.changes.map((c, i) => <li key={i}>{c}</li>)}
              </ul>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
            {HORIZONS.map((hz) => {
              const now = curH[hz] || { count: 0, budget: 0 };
              const was = priH[hz] || { count: 0, budget: 0 };
              return (
                <div key={hz} className={`rounded-lg border p-4 ${H_TONE[hz]}`}>
                  <div className="text-[10px] uppercase tracking-widest font-medium">{displayStatus(hz)}</div>
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    <div>
                      <div className="text-[9px] uppercase text-slate-400">{data.current_year} (now)</div>
                      <div className="font-display text-lg font-semibold text-slate-900 tabular-nums">{fmtCurrency(now.budget)}</div>
                      <div className="text-[10px] text-slate-500">{now.count} assets</div>
                    </div>
                    <div>
                      <div className="text-[9px] uppercase text-slate-400">{prior.year}</div>
                      <div className="font-display text-lg font-semibold text-slate-500 tabular-nums">{fmtCurrency(was.budget)}</div>
                      <div className="text-[10px] text-slate-400">{was.count} assets</div>
                    </div>
                  </div>
                  <div className="mt-2 flex items-center gap-3">
                    <Delta now={now.budget} prior={was.budget} money />
                    <Delta now={now.count} prior={was.count} />
                  </div>
                </div>
              );
            })}
          </div>

          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
              <span className="text-[10px] uppercase tracking-wider text-slate-600 font-medium">Asset classes — {data.current_year} vs {prior.year}</span>
              <span className="text-[11px] text-slate-400">
                Snapshot captured {(prior.captured_at || "").slice(0, 10)} by {prior.captured_by || "—"}
              </span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="prior-year-class-table">
                <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <tr>
                    <th className="text-left font-medium px-4 py-2">Asset class</th>
                    <th className="text-right font-medium px-4 py-2">Immediate now</th>
                    <th className="text-right font-medium px-4 py-2">Immediate {prior.year}</th>
                    <th className="text-right font-medium px-4 py-2">Renewal budget now</th>
                    <th className="text-right font-medium px-4 py-2">Budget {prior.year}</th>
                    <th className="text-right font-medium px-4 py-2">Δ Budget</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.current.classes || []).map((c) => {
                    const p = priClasses[c.asset_group];
                    return (
                      <tr key={c.asset_group} className="border-b border-slate-100">
                        <td className="px-4 py-2 text-slate-800 font-medium">{c.asset_group || "—"}</td>
                        <td className={`px-4 py-2 text-right tabular-nums ${c.immediate_count ? "text-rose-700 font-semibold" : "text-slate-400"}`}>{c.immediate_count || 0}</td>
                        <td className="px-4 py-2 text-right tabular-nums text-slate-500">{p ? p.immediate_count || 0 : "—"}</td>
                        <td className="px-4 py-2 text-right tabular-nums font-medium">{fmtCurrency(c.renewal_budget)}</td>
                        <td className="px-4 py-2 text-right tabular-nums text-slate-500">{p ? fmtCurrency(p.renewal_budget) : "—"}</td>
                        <td className="px-4 py-2 text-right"><Delta now={c.renewal_budget} prior={p?.renewal_budget} money /></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
